import { AccountModel, areAllPropertiesEmpty, BrandsFactory, Button, Checkbox, CircularProgress, ColorTextField, FileUpload, GenericWrapperContext, isValidUrl, TextField } from "@cakemail-org/ui-components-v2"
import { ChangeEvent, ChangeEventHandler, ReactNode, useContext, useEffect, useState } from "react"
import { SelectChangeEvent, Stack } from "@mui/material"
import { Trans, useTranslation } from "react-i18next"
import "./styles.scss"
import { TOnboardingBrandSetup } from "./types"
import { OnboardingContext } from "../../context"
import { TButtonRef } from "@cakemail-org/ui-components-v2/dist/esm/components/button/types"
import { TDropMenuItem } from "@cakemail-org/ui-components-v2/dist/esm/components/dropMenu/types"
import { PUBLIC_DOMAINS } from "../../../../data/publicDomains"
import { DOMAIN_REGEX } from "../../../../data/regexes"

export function OnboardingBrandSetup({ }: TOnboardingBrandSetup) {
    const onboardingContext = useContext(OnboardingContext)
    const { t } = useTranslation()
    const [search, setSearch] = useState<string>("")
    const [file, setFile] = useState<File | null>(null)
    const [fileBase64, setFileBase64] = useState<string>("")
    const [isSearchValid, setIsSearchValid] = useState<boolean>(true)
    const [loading, setLoading] = useState<boolean>(false)

    useEffect(() => {
        const enrichedBrand = onboardingContext?.enrichedBrand
        if (enrichedBrand && enrichedBrand?.colors?.length > 0) {
            onboardingContext.setBrandForm({
                name: enrichedBrand?.title,
                images: {
                    logo: {
                        url: enrichedBrand?.logos?.[0]?.url,
                    }
                },
                theme: {
                    advance: {},
                    base: {
                        "primary-brand-color": enrichedBrand?.colors?.[0]?.hex,
                        "secondary-brand-color": enrichedBrand?.colors?.[1]?.hex
                    }
                }
            })
        }
    }, [onboardingContext?.enrichedBrand])

    function handleResetBrand(): void {
        setFile(null)
        setFileBase64("")
        onboardingContext.setBrandForm({})
    }

    function handleSearchChange(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
        setSearch(event.target.value)
    }

    function handleSearchChangeTimeout(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
        const value = event.target.value
        let isValid = isValidUrl(value) || DOMAIN_REGEX.test(value)

        if (search !== "") {
            const domain = isValid && value.startsWith('http') ? new URL(value).hostname : value
            for (const publicDomain of PUBLIC_DOMAINS) {
                if (domain.includes(publicDomain)) {
                    isValid = false
                    break
                }
            }
            setIsSearchValid(isValid)
            if (isValid) {
                setLoading(true)
                BrandsFactory.enrichBrand({ domain }).then((res) => {
                    if (res.status === "ok") {
                        onboardingContext.setEnrichedBrand(res.brand)
                    }
                    setSearch("")
                    setLoading(false)
                }).catch(() => {
                    setSearch("")
                    setLoading(false)
                })
            }
        } else {
            setIsSearchValid(true)
        }
    }

    function handleLogoDDChange(event: SelectChangeEvent<string>, child: ReactNode, previousValue?: string): void {
        if (event.target.value !== "upload") {
            onboardingContext.setBrandForm({
                ...onboardingContext.brandForm,
                images: {
                    logo: {
                        url: event.target.value
                    }
                }
            })
        }
    }
    function handleFileUploadChange(curFile: File | null): void {
        if (curFile) {
            setFile(curFile)
            const reader = new FileReader()
            reader.onload = (e) => {
                const base64String = e.target?.result as string
                onboardingContext.setBrandForm({
                    ...onboardingContext.brandForm,
                    images: {
                        logo: {
                            url: base64String
                        }
                    }
                })
                setFileBase64(base64String)
            }
            reader.readAsDataURL(curFile)
        }
    }

    function handlePrimaryColorChange(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
        const color = event.target.value
        onboardingContext.setBrandForm({
            ...onboardingContext.brandForm,
            theme: {
                advance: {},
                base: {
                    ...onboardingContext.brandForm.theme?.base,
                    "primary-brand-color": color
                }
            }
        })
    }

    function handleSecondaryColorChange(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
        const color = event.target.value
        onboardingContext.setBrandForm({
            ...onboardingContext.brandForm,
            theme: {
                advance: {},
                base: {
                    ...onboardingContext.brandForm.theme?.base,
                    "secondary-brand-color": color
                }
            }
        })
    }

    function handleNext(e: React.MouseEvent<HTMLButtonElement>, btnRef: TButtonRef) {
        setLoading(true)
        onboardingContext.handleNext(btnRef)
    }
    const enrichedBrand = onboardingContext?.enrichedBrand
    const primaryColor = onboardingContext.brandForm?.theme?.base?.["primary-brand-color"] || ""
    const secondaryColor = onboardingContext.brandForm?.theme?.base?.["secondary-brand-color"] || ""
    const logoUrl = onboardingContext.brandForm?.images?.logo?.url || ""

    let logoOptions: TDropMenuItem[] = []
    if (enrichedBrand && enrichedBrand?.logos?.length > 0) {
        logoOptions = enrichedBrand.logos.map((logo) => ({
            text: logo.url,
            preContent: <img src={logo.url} alt="logo" />,
            value: logo.url,
        }))
        if (file && fileBase64) {
            logoOptions.push({
                text: file.name,
                preContent: <img src={fileBase64} alt="logo" />,
                value: fileBase64,
            })
        }
    }
    const isBrandFormEmpty = areAllPropertiesEmpty(onboardingContext.brandForm)


    return <Stack direction="column" gap={9} flex={1} className={`onboardingBrandSetup ${loading ? "loading" : ""}`}>

        <Stack direction="row" className="onboardingBrandSearch">
            <TextField
                name="name"
                placeholder={t("brand-search-import-placeholder")}
                onChange={handleSearchChange}
                timeout={{
                    callback: handleSearchChangeTimeout,
                    time: 2000
                }}
                value={search}
                iconName="Search18"
                error={!isSearchValid}
                helperText={!isSearchValid ? t("input-validation-url-domain-error") : ""}
            />
            {loading && <CircularProgress color="secondary" size={18} />}
        </Stack>

        <FileUpload
            key={file?.name || "fileUpload"}
            label={t("general-logo")}
            placeholder={<Trans i18nKey="brand-logo-upload-placeholder" />}
            support={t("general-img-upload-support", { maxWidth: 400, maxHeight: 90 })}
            onChange={handleFileUploadChange}
            dropdown={(enrichedBrand && enrichedBrand.logos) ? {
                options: logoOptions,
                placeholder: t("general-logo-DD-placeholder"),
                className: "onboardingBrandLogo",
                onChange: handleLogoDDChange,
                value: logoUrl
            } : undefined}
        />

        <ColorTextField
            name="primary"
            label={t("brand-color-primary-label")}
            placeholder={t("general-hex-color-placeholder")}
            onChange={handlePrimaryColorChange}
            value={primaryColor}
        />

        <ColorTextField
            name="secondary"
            label={t("brand-color-secondary-label")}
            placeholder={t("general-hex-color-placeholder")}
            onChange={handleSecondaryColorChange}
            value={secondaryColor}
        />

        <Stack direction="row" className="onboardingContent" justifyContent="space-between">
            <Stack direction="row" gap={2} alignItems="center">
                {!isBrandFormEmpty && <Button
                    variant="text"
                    color="shade500"
                    onClick={handleResetBrand}
                    sx={{
                        textDecoration: "underline"
                    }}
                >
                    {t("general-clear-brand")}
                </Button>}
            </Stack>
            <Button variant="contained" color={isBrandFormEmpty ? "cloud" : "secondary"} onClick={handleNext} >
                {isBrandFormEmpty ? t("brand-set-up-later-btn") : t("onboarding-complete-signup-btn")}
            </Button>
        </Stack>
    </Stack>
}

export default OnboardingBrandSetup